import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import type { Owner } from '../api/types';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { propertyLine, residenceText, roleLabels } from './labels';
import { ResidenceDialog } from './ResidenceDialog';

// OwnersPanel: Eigentümer des Objekts (OFFENE_FRAGEN B-17) – Hauptansprechpartner und Miteigentümer mit Nummern,
// E-Mail-Adressen, Wohnort und ihren weiteren Objekten (im Gespräch mit ansprechen) – und die weiteren Personen im
// Haushalt (B-20) mit ihren Objekten. Dieselbe Person, dasselbe Objekt und den Haushalt erkennt das System selbst; hier
// gibt es nichts abzugleichen. compact: Lead-Karte der Akquise (Name und Nummern nur bei mehreren Eigentümern, die Karte
// zeigt den Hauptansprechpartner schon).
export function OwnersPanel({ leadId, compact = false }: { leadId: string; compact?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const owners = useQuery({ queryKey: projectKeys.leadOwners(project.id, leadId), queryFn: ({ signal }) => papi.leadOwners(leadId, signal) });
  const [editing, setEditing] = useState<Owner | null>(null);
  const items = owners.data?.items ?? [];
  const household = owners.data?.household?.members ?? [];
  if (items.length === 0 && household.length === 0) return null;
  const several = items.length > 1;
  const newTab = compact ? { target: '_blank', rel: 'noreferrer' } : {};
  return (
    <section className={compact ? 'owners compact' : 'card owners'} aria-label="Eigentümer">
      {!compact && <h2>{several ? `Eigentümer (${items.length})` : 'Eigentümer'}</h2>}
      <ul className="owners-list">
        {items.map((o) => {
          const others = o.properties.filter((p) => p.id !== leadId);
          return (
            <li key={o.id}>
              {(!compact || several) && (
                <p className="owner-name">
                  <Link to={`/p/${project.key}/eigentuemer/${o.id}`} {...newTab}>
                    <strong>{o.name}</strong>
                  </Link>
                  {o.role !== 'OWNER' && <span className="badge badge-muted">{roleLabels[o.role] ?? o.role}</span>}
                  {o.is_primary && several && <span className="badge badge-muted">Hauptansprechpartner</span>}
                </p>
              )}
              {(!compact || !o.is_primary) && (o.phones.length > 0 || o.emails.length > 0) && (
                <p className="owner-channels">
                  {o.phones.map((p) => (
                    <a key={p.number} href={`tel:${p.e164 ?? p.number}`}>
                      {p.number}
                      {p.type === 'MOBILE' && ' (mobil)'}
                    </a>
                  ))}
                  {o.emails.map((e) => (
                    <a key={e} href={`mailto:${e}`}>
                      {e}
                    </a>
                  ))}
                </p>
              )}
              <p className="owner-residence">
                <span className="muted">{compact && several ? 'Wohnort: ' : 'Wohnort des Eigentümers: '}</span>
                {residenceText(o.residence)}
                {o.residence.source === 'AUTO' && o.residence.reason && <span className="muted"> ({o.residence.reason})</span>}
                {can('lead.edit') && (
                  <button type="button" className="btn btn-small" onClick={() => setEditing(o)}>
                    Wohnort ändern
                  </button>
                )}
              </p>
              {others.length > 0 && (
                <div className="owner-properties">
                  <p className="muted small">
                    {others.length === 1 ? 'Weiteres Objekt' : `${others.length} weitere Objekte`} dieses Eigentümers – im Gespräch mit ansprechen:
                  </p>
                  <ul className="plain-list">
                    {others.map((p) => (
                      <li key={p.id}>
                        <Link to={`/p/${project.key}/leads/${p.id}`} {...newTab}>
                          {propertyLine(p)}
                        </Link>{' '}
                        <span className="badge badge-muted">{p.status_label}</span>
                        {(p.area_name || p.field_sales_name) && (
                          <span className="muted"> · {[p.area_name && `Gebiet ${p.area_name}`, p.field_sales_name].filter(Boolean).join(' · ')}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {household.length > 0 && (
        <div className="owner-properties owner-household">
          <p className="muted small">
            Im Haushalt außerdem – Pausen, Mindestabstand und Rückrufe gelten für alle; ihre Objekte im Gespräch mit ansprechen:
          </p>
          <ul className="plain-list">
            {household.map((m) => (
              <li key={m.id}>
                <Link to={`/p/${project.key}/eigentuemer/${m.id}`} {...newTab}>
                  <strong>{m.name}</strong>
                </Link>
                {m.bond && <span className="badge badge-muted">{m.bond}</span>}
                {m.phones.length > 0 && <span className="muted"> · {m.phones.map((p) => p.number).join(', ')}</span>}
                {m.properties.map((p) => (
                  <span key={p.id} className="household-object">
                    {' '}
                    ·{' '}
                    <Link to={`/p/${project.key}/leads/${p.id}`} {...newTab}>
                      {propertyLine(p)}
                    </Link>{' '}
                    <span className="badge badge-muted">{p.status_label}</span>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
      {editing && <ResidenceDialog owner={editing} leadId={leadId} onClose={() => setEditing(null)} />}
    </section>
  );
}
